import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/dateUtils';
import { DATE_PREFIX, FieldRow, clockTime, readString } from './payloadFields';

interface CoverageBreachPayloadProps {
  payload: Record<string, unknown>;
  i18n: {
    _: (d: { id: string; message: string; values?: Record<string, unknown> }) => string;
  };
}

/** Samples shown before "+N more". */
const MAX_SAMPLES = 12;

function readNumber(source: Record<string, unknown>, key: string): number | undefined {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

interface Sample {
  time: string;
  presentChildren: number;
  requiredStaff: number;
  inGroupStaff: number;
  minQualifiedStaff: number;
  inGroupQualifiedStaff: number;
}

function readSamples(payload: Record<string, unknown>): Sample[] {
  const raw = payload.samples;
  if (!Array.isArray(raw)) return [];
  const samples: Sample[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as Record<string, unknown>;
    const time = clockTime(readString(record, 'at'));
    if (!time) continue;
    samples.push({
      time,
      presentChildren: readNumber(record, 'present_children') ?? 0,
      requiredStaff: readNumber(record, 'required_staff') ?? 0,
      inGroupStaff: readNumber(record, 'in_group_staff') ?? 0,
      minQualifiedStaff: readNumber(record, 'min_qualified_staff') ?? 0,
      inGroupQualifiedStaff: readNumber(record, 'in_group_qualified_staff') ?? 0,
    });
  }
  return samples;
}

/**
 * Renders a `coverage_breach` workflow item: a group's in-group staff stayed
 * below the staffing requirement while children were present. Counts only; the
 * payload carries no child. Times are org-local RFC 3339 strings read as text.
 */
export function CoverageBreachPayload({ payload, i18n }: CoverageBreachPayloadProps) {
  const groupName = readString(payload, 'group_name');
  const groupId = readString(payload, 'group_id');
  const leadName = readString(payload, 'lead_name');
  const kind = readString(payload, 'kind');
  const day = readString(payload, 'day');
  const startedAt = clockTime(readString(payload, 'started_at'));
  const endedAt = clockTime(readString(payload, 'ended_at'));
  const presentChildren = readNumber(payload, 'present_children') ?? 0;
  const requiredStaff = readNumber(payload, 'required_staff') ?? 0;
  const inGroupStaff = readNumber(payload, 'in_group_staff') ?? 0;
  const minQualifiedStaff = readNumber(payload, 'min_qualified_staff') ?? 0;
  const inGroupQualifiedStaff = readNumber(payload, 'in_group_qualified_staff') ?? 0;

  const validDay = day && DATE_PREFIX.test(day) ? day.slice(0, 10) : undefined;
  const showQualified = kind === 'qualified' || minQualifiedStaff > 0;

  // Newest first, capped.
  const samples = readSamples(payload).reverse();
  const shownSamples = samples.slice(0, MAX_SAMPLES);
  const hiddenSamples = samples.length - shownSamples.length;

  const kindLabel =
    kind === 'qualified'
      ? i18n._({
          id: 'workflow.payload.coverageBreach.kind.qualified',
          message: 'Too few qualified staff in the group',
        })
      : kind === 'head_count'
        ? i18n._({
            id: 'workflow.payload.coverageBreach.kind.headCount',
            message: 'Too few staff in the group',
          })
        : undefined;

  const staffLine = (inGroup: number, required: number, children: number) =>
    i18n._({
      id: 'workflow.payload.coverageBreach.staffLine',
      message:
        '{inGroup} of {required} in the group · {children, plural, one {# child} other {# children}}',
      values: { inGroup, required, children },
    });

  const groupParam = groupId ? `&group=${encodeURIComponent(groupId)}` : '';

  return (
    <div className="space-y-4">
      {groupName && (
        <FieldRow label={i18n._({ id: 'workflow.payload.coverageBreach.group', message: 'Group' })}>
          <p className="text-sm">{groupName}</p>
        </FieldRow>
      )}

      <FieldRow label={i18n._({ id: 'workflow.payload.coverageBreach.groupLead', message: 'Group lead' })}>
        <p className="text-sm">
          {leadName ??
            i18n._({
              id: 'workflow.payload.coverageBreach.noGroupLead',
              message: 'No group lead set',
            })}
        </p>
      </FieldRow>

      {kindLabel && (
        <FieldRow label={i18n._({ id: 'workflow.payload.coverageBreach.kind', message: 'Shortfall' })}>
          {/* Text-bearing badge, so the kind is never colour-only. */}
          <Badge variant="outline">{kindLabel}</Badge>
        </FieldRow>
      )}

      {startedAt && (
        <FieldRow label={i18n._({ id: 'workflow.payload.coverageBreach.since', message: 'Since' })}>
          <p className="text-sm">
            {validDay
              ? i18n._({
                  id: 'workflow.payload.coverageBreach.sinceValue',
                  message: '{date}, {time}',
                  values: { date: formatDate(validDay), time: startedAt },
                })
              : startedAt}
          </p>
        </FieldRow>
      )}

      <FieldRow label={i18n._({ id: 'workflow.payload.coverageBreach.staff', message: 'Staff' })}>
        <p className="text-sm">{staffLine(inGroupStaff, requiredStaff, presentChildren)}</p>
        {showQualified && (
          <p className="text-sm">
            {i18n._({
              id: 'workflow.payload.coverageBreach.qualifiedLine',
              message: '{inGroup} of {required} qualified in the group',
              values: { inGroup: inGroupQualifiedStaff, required: minQualifiedStaff },
            })}
          </p>
        )}
      </FieldRow>

      <FieldRow label={i18n._({ id: 'workflow.payload.coverageBreach.status', message: 'Status' })}>
        <p className="text-sm">
          {endedAt
            ? i18n._({
                id: 'workflow.payload.coverageBreach.restored',
                message: 'Care ratio met again at {time}',
                values: { time: endedAt },
              })
            : i18n._({
                id: 'workflow.payload.coverageBreach.notRestored',
                message: 'Still not met at the last check',
              })}
        </p>
      </FieldRow>

      {shownSamples.length > 0 && (
        <FieldRow label={i18n._({ id: 'workflow.payload.coverageBreach.samples', message: 'Checks' })}>
          <ul className="space-y-0.5 text-sm" data-testid="coverage-breach-samples">
            {shownSamples.map((sample, index) => (
              <li key={`${sample.time}-${index}`}>
                {i18n._({
                  id: 'workflow.payload.coverageBreach.sampleLine',
                  message:
                    '{time} · {inGroup} of {required} · {children, plural, one {# child} other {# children}}',
                  values: {
                    time: sample.time,
                    inGroup: sample.inGroupStaff,
                    required: sample.requiredStaff,
                    children: sample.presentChildren,
                  },
                })}
              </li>
            ))}
          </ul>
          {hiddenSamples > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              {i18n._({
                id: 'workflow.payload.coverageBreach.moreSamples',
                message: '+{count} more',
                values: { count: hiddenSamples },
              })}
            </p>
          )}
        </FieldRow>
      )}

      {validDay && (
        <div className="flex flex-col gap-1">
          <Link
            to={`/team/presence?date=${validDay}${groupParam}`}
            className="text-sm text-primary underline underline-offset-4"
          >
            {i18n._({
              id: 'workflow.payload.coverageBreach.openPresence',
              message: 'Open group presence for this day',
            })}
          </Link>
          <Link
            to={`/shifts?tab=shifts&date=${validDay}&view=day`}
            className="text-sm text-primary underline underline-offset-4"
          >
            {i18n._({
              id: 'workflow.payload.coverageBreach.openPlanner',
              message: 'Open shift planner for this day',
            })}
          </Link>
        </div>
      )}
    </div>
  );
}
